import { useEffect, useRef, useState } from 'react';
import { statsData } from '@/mocks/homeData';

function AnimatedCounter({ target, suffix = '', duration = 2000 }: { target: string; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            const numericValue = parseFloat(target.replace(/[^0-9.]/g, ''));
            const startTime = Date.now();
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const easeOut = 1 - Math.pow(1 - progress, 3);
              setCount(Math.floor(numericValue * easeOut));
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setCount(numericValue);
              }
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  const displayValue = target.includes('%') ? `${count}%` : target.includes('+') ? `${count}+` : `${count}`;

  return <div ref={ref}>{hasAnimated ? displayValue : '0'}</div>;
}

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 20,
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden bg-firmai-dark"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,217,163,0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Glow Effects - parallax */}
      <div
        className="absolute top-1/4 right-1/4 w-96 h-96 bg-firmai-green/10 rounded-full blur-3xl transition-transform duration-300 ease-out"
        style={{ transform: `translate(${mousePos.x}px, ${mousePos.y}px)` }}
      />
      <div
        className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-firmai-blue/10 rounded-full blur-3xl transition-transform duration-300 ease-out"
        style={{ transform: `translate(${-mousePos.x}px, ${-mousePos.y}px)` }}
      />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          {/* Left Content */}
          <div className="w-full lg:w-[55%] space-y-8">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
                工业智能化
                <br />
                <span className="text-firmai-text">能碳管理中心</span>
                <br />
                <span className="bg-gradient-to-r from-firmai-green to-firmai-blue bg-clip-text text-transparent">
                  数字化整体解决方案
                </span>
              </h1>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              <p className="text-firmai-text text-base md:text-lg leading-relaxed">
                满足工信部建设指南 · 12项核心功能 · 工业企业与园区专属
              </p>
              <p className="text-firmai-muted text-sm md:text-base mt-3 leading-relaxed">
                Firmai 专注工业智能化赋能，覆盖能耗查询、能效对标、能流分析、碳排放核算、产品碳足迹、供应链碳管理、碳资产管理等全部 12 项数字化能碳管理中心核心功能，助力工业企业和园区完成数字化转型。
              </p>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap group"
              >
                免费方案咨询
                <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
              <button
                onClick={() => {
                  const modal = document.getElementById('video-modal');
                  if (modal) modal.classList.remove('hidden');
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-md hover:border-white/60 hover:bg-white/5 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap group"
              >
                <span className="w-5 h-5 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <i className="ri-play-circle-line" />
                </span>
                观看演示视频
              </button>
            </div>

            {/* Three Core Business Quick Access */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-350 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {[
                { icon: 'ri-government-line', title: '工信部碳排申报', sub: '12项功能 · 直连官方平台', href: '/business', color: 'border-firmai-green/30 hover:border-firmai-green/60' },
                { icon: 'ri-robot-2-line', title: '工业AI智能化', sub: '工厂落地 · AI预测运维', href: '/business', color: 'border-cyan-500/30 hover:border-cyan-500/60' },
                { icon: 'ri-global-line', title: 'CCER/CBAM合规', sub: '注册系统直连 · 欧盟合规', href: '/business', color: 'border-violet-500/30 hover:border-violet-500/60' },
              ].map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  className={`flex items-center gap-3 bg-white/5 backdrop-blur-sm border ${item.color} rounded-lg p-3 transition-all duration-300 cursor-pointer group`}
                >
                  <span className="w-8 h-8 flex items-center justify-center text-firmai-green bg-firmai-green/10 rounded-lg shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <div>
                    <div className="text-white text-xs font-semibold leading-snug">{item.title}</div>
                    <div className="text-firmai-muted text-xs mt-0.5">{item.sub}</div>
                  </div>
                </a>
              ))}
            </div>

            {/* Stats with animated counters */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-300 grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
              {statsData.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 text-center hover:border-firmai-green/30 hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className="text-firmai-green font-display font-bold text-2xl md:text-3xl group-hover:scale-105 transition-transform duration-300">
                    <AnimatedCounter target={stat.number} />
                  </div>
                  <div className="text-firmai-muted text-xs md:text-sm mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div className="w-full lg:w-[45%] relative">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 relative">
              {/* Dashboard Image */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-firmai-green/10 border border-firmai-border/50 group">
                <img
                  src="https://readdy.ai/api/search-image?query=dark%20themed%20energy%20management%20dashboard%20interface%20with%20real-time%20monitoring%20charts%20carbon%20emission%20metrics%20green%20accent%20colors%20modern%20UI%20design%20professional%20enterprise%20software%20screenshot&width=600&height=420&seq=dashboard1&orientation=landscape"
                  alt="Firmai 实时监测仪表盘"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-firmai-dark/40 to-transparent" />
              </div>

              {/* Floating Cards with enhanced animations */}
              <div className="absolute -top-4 -right-4 bg-firmai-card border border-red-500/30 rounded-lg p-3 shadow-lg animate-float hover:scale-105 transition-transform duration-300 cursor-pointer">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-white text-xs font-medium">实时告警</span>
                </div>
                <p className="text-firmai-muted text-xs mt-1">设备A3能耗异常</p>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-firmai-card border border-firmai-green/30 rounded-lg p-3 shadow-lg animate-float hover:scale-105 transition-transform duration-300 cursor-pointer" style={{ animationDelay: '2s' }}>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-firmai-green">
                    <i className="ri-check-double-line" />
                  </span>
                  <span className="text-white text-xs font-medium">AI优化建议</span>
                </div>
                <p className="text-firmai-muted text-xs mt-1">建议调整空调运行时段</p>
              </div>

              {/* New floating card */}
              <div className="absolute top-1/2 -right-6 bg-firmai-card border border-firmai-blue/30 rounded-lg p-3 shadow-lg animate-float hover:scale-105 transition-transform duration-300 cursor-pointer" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-firmai-blue">
                    <i className="ri-leaf-line" />
                  </span>
                  <span className="text-white text-xs font-medium">碳减排达标</span>
                </div>
                <p className="text-firmai-muted text-xs mt-1">本月减排 12.5%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      <div id="video-modal" className="hidden fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="relative max-w-3xl w-full bg-firmai-card rounded-xl overflow-hidden animate-scale-in">
          <button
            onClick={() => document.getElementById('video-modal')?.classList.add('hidden')}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-white hover:text-firmai-green transition-colors hover:rotate-90 duration-300"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div className="aspect-video bg-firmai-dark flex items-center justify-center">
            <div className="text-center">
              <span className="w-16 h-16 flex items-center justify-center mx-auto text-firmai-green mb-4 animate-pulse">
                <i className="ri-play-circle-line text-5xl" />
              </span>
              <p className="text-firmai-text">演示视频即将上线</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}